import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { gpConnectConstants } from 'locale/gpConnect'
import { getNHSFormattedNumber } from 'screens/Residents/screens/ResidentProfileScreen/utils/utils'
import { Link } from 'react-router-dom'

export const SupersededWarning = ({
  recordsNHSNumber,
  isFromCanada,
  residentId,
}: SupersededWarningProps) => {
  const { t } = useTranslation(NAMESPACE.GP_CONNECT)

  if (recordsNHSNumber && residentId) {
    const formattedNhsNumber = getNHSFormattedNumber(
      recordsNHSNumber,
      isFromCanada
    )
    return (
      <>
        <WarningText level={2} weight="regular" block>
          {t(gpConnectConstants.SUPERSEDED_WARNING, {
            nhsNumber: formattedNhsNumber,
          })}
        </WarningText>
        <div>
          <WarningText level={2} weight="regular">
            {t(gpConnectConstants.PLEASE_UPDATE)}
          </WarningText>
          <StyledLink to={`/residents/${residentId}/care-related-information`}>
            {t(gpConnectConstants.HERE)}
          </StyledLink>
        </div>
      </>
    )
  }
  return null
}

export const FamilyNameWarning = ({
  familyName = '',
  residentId,
}: FamilyNameWarningProps) => {
  const { t } = useTranslation(NAMESPACE.GP_CONNECT)

  if (residentId) {
    return (
      <>
        <WarningText level={2} weight="regular" block>
          {t(gpConnectConstants.FAMILY_NAME_MISMATCH, {
            familyName,
          })}
        </WarningText>
        <div>
          <WarningText level={2} weight="regular">
            {t(gpConnectConstants.UPDATE_RECORDS)}
          </WarningText>
          <StyledLink to={`/residents/${residentId}/personal-information`}>
            {t(gpConnectConstants.HERE)}
          </StyledLink>
        </div>
      </>
    )
  }
  return null
}

export const GivenNameWarning = ({
  givenName = '',
  residentId,
}: GivenNameWarningProps) => {
  const { t } = useTranslation(NAMESPACE.GP_CONNECT)

  if (residentId) {
    return (
      <WarningText level={2} weight="regular" block>
        {t(gpConnectConstants.GIVEN_NAME_MISMATCH, {
          givenName,
        })}
        <div>
          <WarningText level={2} weight="regular">
            {t(gpConnectConstants.UPDATE_RECORDS)}
          </WarningText>
          <StyledLink to={`/residents/${residentId}/personal-information`}>
            {t(gpConnectConstants.HERE)}
          </StyledLink>
        </div>
      </WarningText>
    )
  }
  return null
}

export const GenderWarning = ({
  gender = '',
  residentId,
}: GenderWarningProps) => {
  const { t } = useTranslation(NAMESPACE.GP_CONNECT)

  if (residentId) {
    return (
      <>
        <WarningText level={2} weight="regular" block>
          {t(gpConnectConstants.GENDER_MISMATCH, {
            gender,
          })}
        </WarningText>
        <div>
          <WarningText level={2} weight="regular">
            {t(gpConnectConstants.PLEASE_UPDATE)}
          </WarningText>
          <StyledLink to={`/residents/${residentId}/personal-information`}>
            {t(gpConnectConstants.HERE)}
          </StyledLink>
        </div>
      </>
    )
  }
  return null
}

export const SexWarning = ({ sex = '', residentId }: SexWarningProps) => {
  const { t } = useTranslation(NAMESPACE.GP_CONNECT)

  if (residentId) {
    return (
      <>
        <WarningText level={2} weight="regular" block>
          {t(gpConnectConstants.SEX_MISMATCH, {
            sex,
          })}
        </WarningText>
        <div>
          <WarningText level={2} weight="regular">
            {t(gpConnectConstants.PLEASE_UPDATE)}
          </WarningText>
          <StyledLink to={`/residents/${residentId}/personal-information`}>
            {t(gpConnectConstants.HERE)}
          </StyledLink>
        </div>
      </>
    )
  }
  return null
}

export const DOBWarning = ({ dob = '', residentId }: DOBWarningProps) => {
  const { t } = useTranslation(NAMESPACE.GP_CONNECT)

  if (residentId) {
    return (
      <>
        <WarningText level={2} weight="regular" block>
          {t(gpConnectConstants.DOB_MISMATCH, {
            dob,
          })}
        </WarningText>
        <div>
          <WarningText level={2} weight="regular">
            {t(gpConnectConstants.PLEASE_UPDATE)}
          </WarningText>
          <StyledLink to={`/residents/${residentId}/personal-information`}>
            {t(gpConnectConstants.HERE)}
          </StyledLink>
        </div>
      </>
    )
  }
  return null
}

type SupersededWarningProps = {
  recordsNHSNumber: string
  isFromCanada: boolean
  residentId?: string
}

type FamilyNameWarningProps = {
  familyName?: string
  residentId?: string
}

type GivenNameWarningProps = {
  givenName?: string
  residentId?: string
}

type GenderWarningProps = {
  gender?: string
  residentId?: string
}

type SexWarningProps = {
  sex?: string
  residentId?: string
}

type DOBWarningProps = {
  dob?: string
  residentId?: string
}

interface WarningTextProps {
  block?: boolean
}

const WarningText = styled(Text)<WarningTextProps>`
  color: ${(props) => props.theme.color.status.danger};
  display: ${(props) => (props.block ? 'block' : 'inline')};
`

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.color.status.primary};
  font-weight: ${({ theme }) => theme.font.weight.regular};
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
`
